/**
 * Lazy wrapper around tesseract.js.
 *
 * Dynamic-imported so the WASM blob stays out of the initial bundle.
 * Language data is served locally from `public/tesseract/` (fra + eng,
 * pre-bundled in Phase 10) and cached by the service worker for offline use.
 */

export interface OcrProgress {
  status: string;
  /** 0..1 */
  progress: number;
}

export interface OcrResult {
  text: string;
  /** Mean OCR confidence reported by Tesseract (0..100). */
  confidence: number;
}

let workerPromise: Promise<{
  recognize: (img: Blob | string) => Promise<OcrResult>;
  terminate: () => Promise<void>;
}> | null = null;

/**
 * Run OCR on an image file/blob in French+English.
 *
 * Workers are pooled — successive calls reuse the same Tesseract instance
 * (avoids re-downloading the language models).
 */
export async function ocrImage(
  image: Blob | File,
  onProgress?: (p: OcrProgress) => void,
): Promise<OcrResult> {
  if (!workerPromise) {
    workerPromise = (async () => {
      const { createWorker } = await import('tesseract.js');
      // langPath points at the pre-bundled `fra.traineddata.gz` /
      // `eng.traineddata.gz` files served from /tesseract/ (cached by SW
      // for offline use).
      const worker = await createWorker('fra+eng', 1, {
        langPath: '/tesseract',
        logger: (m: OcrProgress) => onProgress?.(m),
      });

      return {
        recognize: async (img) => {
          const res = await worker.recognize(img);
          return {
            text: res.data.text ?? '',
            confidence: res.data.confidence ?? 0,
          };
        },
        terminate: async () => {
          await worker.terminate();
        },
      };
    })();
  }

  const worker = await workerPromise;
  return worker.recognize(image);
}

/** Tear down the pooled worker. Call on app teardown or when memory is tight. */
export async function terminateOcrWorker(): Promise<void> {
  if (!workerPromise) return;
  try {
    const w = await workerPromise;
    await w.terminate();
  } finally {
    workerPromise = null;
  }
}
